import { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

// material-ui
import Alert from '@mui/material/Alert';
// import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
// import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTheme, alpha } from '@mui/material/styles';

// ant design icons
import DisconnectOutlined from '@ant-design/icons/DisconnectOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import SaveOutlined from '@ant-design/icons/SaveOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import AimOutlined from '@ant-design/icons/AimOutlined';

// third party
import { format, differenceInDays } from 'date-fns';

// project imports
import MainCard from 'components/MainCard';
import { CSVExport, DebouncedInput, TablePagination } from 'components/third-party/react-table';
import { openSnackbar } from 'api/snackbar';
import useOnlineStatus from 'hooks/useOnlineStatus';

// ant design icons for offline
import LeftOutlined from '@ant-design/icons/LeftOutlined';
import SpeechTextField from 'components/SpeechTextField';
import TargetStatusHistoryButton from 'sections/shared/child-detail/TargetStatusHistory';

const EMPTY_TARGETS = [];

const SkillTrackingMode = ({
  viewMode = 'individual',
  onDataChange,
  targets = EMPTY_TARGETS,
  // Offline mode props
  isOfflineMode = false,
  childId,
  childName,
  therapistId,
  therapistName,
  sessionId,
  onSave,
  onBack
}) => {
  const theme = useTheme();
  const intl = useIntl();

  // Get actual network status - auto-detect offline regardless of isOfflineMode prop
  const { isOnline } = useOnlineStatus();

  // Domain options for new targets
  const domainOptions = [
    intl.formatMessage({ id: 'form.skillTracking.domain.mand' }),
    intl.formatMessage({ id: 'form.skillTracking.domain.tact' }),
    intl.formatMessage({ id: 'form.skillTracking.domain.listenerResponding' }),
    intl.formatMessage({ id: 'form.skillTracking.domain.motorImitation' }),
    intl.formatMessage({ id: 'form.skillTracking.domain.visualPerformance' }),
    intl.formatMessage({ id: 'form.skillTracking.domain.social' }),
    intl.formatMessage({ id: 'form.skillTracking.domain.playSkills' }),
    intl.formatMessage({ id: 'form.skillTracking.domain.fineMotor' }),
    intl.formatMessage({ id: 'form.skillTracking.domain.grossMotor' }),
    intl.formatMessage({ id: 'form.skillTracking.domain.selfHelp' }),
    intl.formatMessage({ id: 'form.skillTracking.domain.intraverbal' })
  ];

  // Initialize with targets from configuration
  const [allTargets, setAllTargets] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [editingCardId, setEditingCardId] = useState(null); // Track which card is being edited
  const [sessionNotes, setSessionNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Ref to skip auto-save until user actually modifies something
  const initialRenderRef = useRef(true);
  const userModifiedRef = useRef(false);

  // Handle save - uses offline-first pattern (always saves to IndexedDB, syncs in background)
  const handleSave = async (showNotification = true) => {
    setSaving(true);
    try {
      // Don't save if no data
      if (allTargets.length === 0) {
        setSaving(false);
        return;
      }

      const formData = {
        targets: allTargets.map((target) => ({
          targetId: target.id,
          targetName: target.target || target.name,
          domain: target.domain,
          dateIntroduced: target.dateIntroduced,
          dateMastered: target.dateMastered,
          daysToMastery: target.daysToMastery,
          location: target.location,
          people: target.people,
          stimuli: target.stimuli
        })),
        totalTargets: allTargets.length,
        masteredTargets: allTargets.filter((t) => t.dateMastered).length,
        sessionNotes
      };

      // Notify parent to persist via its upsert pattern (persistToIndexedDB)
      if (onDataChange) {
        onDataChange('skill-tracking', formData);
      }

      if (showNotification) {
        openSnackbar({
          open: true,
          message: isOnline
            ? intl.formatMessage({ id: 'form.msg.dataSaved' }, { formName: intl.formatMessage({ id: 'form.skillTracking.title' }) })
            : intl.formatMessage({ id: 'form.msg.dataSavedOffline' }, { formName: intl.formatMessage({ id: 'form.skillTracking.title' }) }),
          variant: 'alert',
          alert: { color: 'success' }
        });
      }

      if (onSave) onSave();
      if (isOfflineMode && onBack) onBack();
    } catch (error) {
      console.error('Error saving entry:', error);
      if (showNotification) {
        openSnackbar({
          open: true,
          message: intl.formatMessage({ id: 'form.msg.saveFailed' }),
          variant: 'alert',
          alert: { color: 'error' }
        });
      }
    } finally {
      setSaving(false);
    }
  };

  // Alias for backward compatibility with existing UI buttons
  const handleOfflineSave = handleSave;

  // Initialize allTargets when targets prop changes
  useEffect(() => {
    if (targets.length > 0) {
      setAllTargets(targets.map((t) => ({ ...t, isNew: false })));
    }
  }, [targets]);

  // Auto-save: notify parent when form data changes so it can debounce and persist
  useEffect(() => {
    // Skip the initial render to avoid saving empty/default state
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      return;
    }

    // Don't auto-save until user has actually modified something
    if (!userModifiedRef.current) return;

    // Don't notify if no data has been entered yet
    if (allTargets.length === 0) return;

    // Collect current form data (mirrors handleSave structure)
    const formData = {
      targets: allTargets.map((target) => ({
        targetId: target.id,
        targetName: target.target || target.name,
        domain: target.domain,
        dateIntroduced: target.dateIntroduced,
        dateMastered: target.dateMastered,
        daysToMastery: target.daysToMastery,
        location: target.location,
        people: target.people,
        stimuli: target.stimuli
      })),
      totalTargets: allTargets.length,
      masteredTargets: allTargets.filter((t) => t.dateMastered).length,
      sessionNotes
    };

    if (onDataChange) onDataChange('skill-tracking', formData);
  }, [allTargets, sessionNotes]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddTarget = () => {
    userModifiedRef.current = true;
    const newTarget = {
      id: Date.now(),
      target: '',
      domain: '',
      dateIntroduced: null,
      dateMastered: null,
      daysToMastery: '-',
      location: '',
      people: '',
      stimuli: '',
      isNew: true // Flag to highlight new rows
    };
    setAllTargets([newTarget, ...allTargets]); // Add at top

    // Auto-save handled by useEffect on state changes → onDataChange → parent's persistToIndexedDB (upsert)
  };

  const handleRemoveTarget = (id) => {
    userModifiedRef.current = true;
    setAllTargets(allTargets.filter((t) => t.id !== id));

    // Auto-save handled by useEffect on state changes → onDataChange → parent's persistToIndexedDB (upsert)
  };

  const handleEditCard = (id) => {
    setEditingCardId(id);
  };

  const handleSaveCard = (id) => {
    // Mark the target as no longer new and exit edit mode
    setAllTargets(allTargets.map((t) => (t.id === id ? { ...t, isNew: false } : t)));
    setEditingCardId(null);

    // Auto-save handled by useEffect on state changes → onDataChange → parent's persistToIndexedDB (upsert)
  };

  const handleCancelEdit = (id, isNew) => {
    if (isNew) {
      // If it's a new record being cancelled, remove it
      handleRemoveTarget(id);
    }
    setEditingCardId(null);
  };

  const handleUpdateTarget = (id, field, value) => {
    userModifiedRef.current = true;
    setAllTargets(
      allTargets.map((t) => {
        if (t.id === id) {
          const updated = { ...t, [field]: value };
          // Recalculate days if dates changed
          if (field === 'dateIntroduced' || field === 'dateMastered') {
            updated.daysToMastery = calculateDaysToMastery(
              field === 'dateIntroduced' ? value : t.dateIntroduced,
              field === 'dateMastered' ? value : t.dateMastered
            );
          }
          return updated;
        }
        return t;
      })
    );

    // Auto-save handled by useEffect on state changes → onDataChange → parent's persistToIndexedDB (upsert)
  };

  const calculateDaysToMastery = (dateIntroduced, dateMastered) => {
    if (!dateIntroduced || !dateMastered) return '-';
    const days = differenceInDays(new Date(dateMastered), new Date(dateIntroduced));
    return days >= 0 ? days : '-';
  };

  // Filter all targets (including new entries)
  const filteredTargets = useMemo(() => {
    let filtered = allTargets;

    // Filter by date range (only for rows with dates)
    if (startDate || endDate) {
      filtered = filtered.filter((row) => {
        if (!row.dateIntroduced) return true; // Keep new entries
        const rowDate = new Date(row.dateIntroduced);
        if (startDate && endDate) {
          return rowDate >= startDate && rowDate <= endDate;
        } else if (startDate) {
          return rowDate >= startDate;
        } else if (endDate) {
          return rowDate <= endDate;
        }
        return true;
      });
    }

    // Filter by search text
    if (globalFilter) {
      const lowerFilter = globalFilter.toLowerCase();
      filtered = filtered.filter(
        (row) =>
          (row.target?.toLowerCase() || '').includes(lowerFilter) ||
          (row.domain?.toLowerCase() || '').includes(lowerFilter) ||
          (row.location?.toLowerCase() || '').includes(lowerFilter) ||
          (row.people?.toLowerCase() || '').includes(lowerFilter) ||
          (row.stimuli?.toLowerCase() || '').includes(lowerFilter)
      );
    }

    return filtered;
  }, [allTargets, globalFilter, startDate, endDate]);

  // Paginated data
  const paginatedData = useMemo(() => {
    const startIdx = pagination.pageIndex * pagination.pageSize;
    const endIdx = startIdx + pagination.pageSize;
    return filteredTargets.slice(startIdx, endIdx);
  }, [filteredTargets, pagination]);

  // Empty state if no targets configured
  if (allTargets.length === 0 && targets.length === 0) {
    return (
      <MainCard>
        <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
          <AimOutlined style={{ fontSize: '4rem', color: theme.palette.grey[300] }} />
          <Typography variant="h5" color="text.secondary">
            {intl.formatMessage({ id: 'form.skillTracking.noTargetsConfigured' })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {intl.formatMessage({ id: 'form.skillTracking.noTargetsYet' })}
          </Typography>
          <Button variant="contained" startIcon={<PlusOutlined />} onClick={handleAddTarget}>
            {intl.formatMessage({ id: 'form.skillTracking.addFirstTarget' })}
          </Button>
        </Stack>
      </MainCard>
    );
  }

  // Individual View - Card-based view for mobile/individual mode
  if (viewMode === 'individual') {
    return (
      <Box sx={isOfflineMode ? { p: { xs: 2, sm: 3 } } : {}}>
        {/* Network Lost Banner - shown when not in explicit offline mode but network is down */}
        {!isOfflineMode && !isOnline && (
          <Alert severity="info" icon={<DisconnectOutlined />} sx={{ mb: 2 }}>
            <Typography variant="body2">{intl.formatMessage({ id: 'form.msg.offlineNote' })}</Typography>
          </Alert>
        )}

        {/* Offline Mode Header */}
        {isOfflineMode && (
          <MainCard sx={{ mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Button variant="text" startIcon={<LeftOutlined />} onClick={onBack}>
                  {intl.formatMessage({ id: 'form.button.back' })}
                </Button>
                <Box>
                  <Typography variant="h5">{intl.formatMessage({ id: 'form.skillTracking.headerTitle' }, { childName })}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {intl.formatMessage({ id: 'form.label.offlineMode' })}
                  </Typography>
                </Box>
              </Stack>
              <Button
                variant="contained"
                startIcon={<SaveOutlined />}
                onClick={handleOfflineSave}
                disabled={saving || allTargets.length === 0}
              >
                {saving ? intl.formatMessage({ id: 'form.button.saving' }) : intl.formatMessage({ id: 'form.button.saveOffline' })}
              </Button>
            </Stack>
          </MainCard>
        )}

        <Stack spacing={3}>
          <MainCard>
            <Stack spacing={3}>
              {/* Header with Add Button */}
              <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                <Typography variant="h5">{intl.formatMessage({ id: 'form.skillTracking.targetTracking' })}</Typography>
                <Button variant="contained" size="small" startIcon={<PlusOutlined />} onClick={handleAddTarget}>
                  {intl.formatMessage({ id: 'form.skillTracking.addNew' })}
                </Button>
              </Stack>

              {/* Search */}
              <DebouncedInput
                value={globalFilter ?? ''}
                onFilterChange={(value) => setGlobalFilter(String(value))}
                placeholder={intl.formatMessage({ id: 'form.skillTracking.searchPlaceholder' })}
                sx={{ width: '100%' }}
              />

              {/* Target Cards */}
              <Stack spacing={2}>
                {paginatedData.length === 0 ? (
                  <Paper sx={{ p: 6, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {intl.formatMessage({ id: 'form.skillTracking.noRecordsFound' })}
                    </Typography>
                  </Paper>
                ) : (
                  paginatedData.map((row, index) => {
                    const isEditing = row.isNew || editingCardId === row.id;

                    return (
                      <Paper
                        key={row.id}
                        sx={{
                          p: 3,
                          border: 1,
                          borderColor: 'divider',
                          bgcolor: isEditing ? 'grey.50' : 'inherit'
                        }}
                      >
                        <Stack spacing={3}>
                          {/* Header */}
                          {!isEditing ? (
                            <Stack spacing={1}>
                              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                <Typography variant="subtitle2" fontWeight={600} sx={{ flexGrow: 1, pr: 1 }}>
                                  {row.target}
                                </Typography>
                                <IconButton size="small" color="primary" onClick={() => handleEditCard(row.id)} sx={{ flexShrink: 0 }}>
                                  <EditOutlined />
                                </IconButton>
                              </Stack>
                              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.5 }}>
                                {row.domain && <Chip label={row.domain} size="small" color="secondary" variant="filled" />}
                                {row.dateIntroduced && (
                                  <Chip
                                    label={intl.formatMessage(
                                      { id: 'form.skillTracking.introduced' },
                                      { date: format(new Date(row.dateIntroduced), 'MMM dd, yyyy') }
                                    )}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                )}
                                {typeof row.daysToMastery === 'number' && (
                                  <Chip
                                    label={intl.formatMessage({ id: 'form.skillTracking.daysToMasteryValue' }, { days: row.daysToMastery })}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                  />
                                )}
                              </Stack>
                            </Stack>
                          ) : null}

                          {/* Target Name */}
                          {isEditing && (
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'primary.main' }}>
                                {intl.formatMessage({ id: 'form.skillTracking.targetDescription' })}
                              </Typography>
                              <TextField
                                fullWidth
                                value={row.target || ''}
                                onChange={(e) => handleUpdateTarget(row.id, 'target', e.target.value)}
                                placeholder={intl.formatMessage({ id: 'form.skillTracking.enterTarget' })}
                              />
                            </Box>
                          )}

                          {/* Domain */}
                          {isEditing && (
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'primary.main' }}>
                                {intl.formatMessage({ id: 'form.skillTracking.domain' })}
                              </Typography>
                              <FormControl fullWidth>
                                <Select
                                  value={row.domain || ''}
                                  displayEmpty
                                  onChange={(e) => handleUpdateTarget(row.id, 'domain', e.target.value)}
                                >
                                  <MenuItem value="" disabled>
                                    {intl.formatMessage({ id: 'form.skillTracking.selectDomain' })}
                                  </MenuItem>
                                  {domainOptions.map((d) => (
                                    <MenuItem key={d} value={d}>
                                      {d}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                          )}

                          {/* Dates */}
                          {isEditing && (
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                              <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                  label={intl.formatMessage({ id: 'form.skillTracking.dateIntroduced' })}
                                  value={row.dateIntroduced ? new Date(row.dateIntroduced) : null}
                                  onChange={(newValue) => handleUpdateTarget(row.id, 'dateIntroduced', newValue)}
                                  slotProps={{ textField: { fullWidth: true } }}
                                />
                                <DatePicker
                                  label={intl.formatMessage({ id: 'form.skillTracking.dateMastered' })}
                                  value={row.dateMastered ? new Date(row.dateMastered) : null}
                                  onChange={(newValue) => handleUpdateTarget(row.id, 'dateMastered', newValue)}
                                  slotProps={{ textField: { fullWidth: true } }}
                                />
                              </LocalizationProvider>
                            </Stack>
                          )}

                          {/* Location */}
                          {isEditing && (
                            <TextField
                              fullWidth
                              label={intl.formatMessage({ id: 'form.skillTracking.location' })}
                              value={row.location || ''}
                              onChange={(e) => handleUpdateTarget(row.id, 'location', e.target.value)}
                              placeholder={intl.formatMessage({ id: 'form.skillTracking.locationPlaceholder' })}
                            />
                          )}

                          {/* People and Stimuli */}
                          {isEditing && (
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                              <TextField
                                fullWidth
                                label={intl.formatMessage({ id: 'form.skillTracking.people' })}
                                value={row.people || ''}
                                onChange={(e) => handleUpdateTarget(row.id, 'people', e.target.value)}
                                placeholder={intl.formatMessage({ id: 'form.skillTracking.peoplePlaceholder' })}
                              />
                              <TextField
                                fullWidth
                                label={intl.formatMessage({ id: 'form.skillTracking.stimuli' })}
                                value={row.stimuli || ''}
                                onChange={(e) => handleUpdateTarget(row.id, 'stimuli', e.target.value)}
                                placeholder={intl.formatMessage({ id: 'form.skillTracking.stimuliPlaceholder' })}
                              />
                            </Stack>
                          )}

                          {/* Read-only view */}
                          {!isEditing && (
                            <>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                  <Paper sx={{ p: 2, bgcolor: 'primary.lighter', height: '100%' }}>
                                    <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ mb: 1, display: 'block' }}>
                                      {intl.formatMessage({ id: 'form.skillTracking.dateIntroducedSection' })}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                      {row.dateIntroduced ? format(new Date(row.dateIntroduced), 'MMM dd, yyyy') : '-'}
                                    </Typography>
                                  </Paper>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <Paper sx={{ p: 2, bgcolor: 'success.lighter', height: '100%' }}>
                                    <Typography variant="caption" fontWeight={700} color="success.main" sx={{ mb: 1, display: 'block' }}>
                                      {intl.formatMessage({ id: 'form.skillTracking.dateMasteredSection' })}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                      {row.dateMastered ? format(new Date(row.dateMastered), 'MMM dd, yyyy') : '-'}
                                    </Typography>
                                  </Paper>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <Paper sx={{ p: 2, bgcolor: 'warning.lighter', height: '100%' }}>
                                    <Typography variant="caption" fontWeight={700} color="warning.main" sx={{ mb: 1, display: 'block' }}>
                                      {intl.formatMessage({ id: 'form.skillTracking.daysToMastery' })}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                      {typeof row.daysToMastery === 'number'
                                        ? intl.formatMessage({ id: 'form.skillTracking.daysToMasteryDays' }, { days: row.daysToMastery })
                                        : '-'}
                                    </Typography>
                                  </Paper>
                                </Grid>
                              </Grid>

                              {(row.location || row.people || row.stimuli) && (
                                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                  <Grid container spacing={2}>
                                    {row.location && (
                                      <Grid item xs={12} sm={4}>
                                        <Typography
                                          variant="caption"
                                          fontWeight={700}
                                          color="text.secondary"
                                          sx={{ mb: 0.5, display: 'block' }}
                                        >
                                          {intl.formatMessage({ id: 'form.skillTracking.locationSection' })}
                                        </Typography>
                                        <Typography variant="body2">{row.location}</Typography>
                                      </Grid>
                                    )}
                                    {row.people && (
                                      <Grid item xs={12} sm={4}>
                                        <Typography
                                          variant="caption"
                                          fontWeight={700}
                                          color="text.secondary"
                                          sx={{ mb: 0.5, display: 'block' }}
                                        >
                                          {intl.formatMessage({ id: 'form.skillTracking.peopleSection' })}
                                        </Typography>
                                        <Typography variant="body2">{row.people}</Typography>
                                      </Grid>
                                    )}
                                    {row.stimuli && (
                                      <Grid item xs={12} sm={4}>
                                        <Typography
                                          variant="caption"
                                          fontWeight={700}
                                          color="text.secondary"
                                          sx={{ mb: 0.5, display: 'block' }}
                                        >
                                          {intl.formatMessage({ id: 'form.skillTracking.stimuliSection' })}
                                        </Typography>
                                        <Typography variant="body2">{row.stimuli}</Typography>
                                      </Grid>
                                    )}
                                  </Grid>
                                </Paper>
                              )}
                            </>
                          )}

                          {/* Action Buttons */}
                          {isEditing && (
                            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ pt: 1 }}>
                              <Button variant="outlined" onClick={() => handleCancelEdit(row.id, row.isNew)}>
                                {intl.formatMessage({ id: 'common.cancel' })}
                              </Button>
                              <Button
                                variant="contained"
                                startIcon={<CheckOutlined />}
                                onClick={() => handleSaveCard(row.id)}
                                disabled={!row.target}
                              >
                                {row.isNew
                                  ? intl.formatMessage({ id: 'form.skillTracking.addRecord' })
                                  : intl.formatMessage({ id: 'form.skillTracking.update' })}
                              </Button>
                            </Stack>
                          )}
                        </Stack>
                      </Paper>
                    );
                  })
                )}
              </Stack>

              {/* Pagination */}
              <Stack sx={{ pt: 2 }}>
                <TablePagination
                  getPageCount={() => Math.ceil(filteredTargets.length / pagination.pageSize)}
                  setPageIndex={(pageIndex) => setPagination({ ...pagination, pageIndex })}
                  setPageSize={(pageSize) => setPagination({ pageIndex: 0, pageSize })}
                  getState={() => ({ pagination })}
                />
              </Stack>
            </Stack>
          </MainCard>

          {/* Session Notes - Offline Mode Only */}
          {isOfflineMode && (
            <MainCard>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                {intl.formatMessage({ id: 'form.label.sessionNotes' })}
              </Typography>
              <SpeechTextField
                fullWidth
                multiline
                rows={3}
                placeholder={intl.formatMessage({ id: 'form.label.notesPlaceholder' })}
                value={sessionNotes}
                onChange={(e) => {
                  userModifiedRef.current = true;
                  setSessionNotes(e.target.value);
                }}
              />
            </MainCard>
          )}
        </Stack>
      </Box>
    );
  }

  // Grid View - Table view for desktop/grid mode
  return (
    <MainCard>
      {/* Network Lost Banner - shown when not in explicit offline mode but network is down */}
      {!isOfflineMode && !isOnline && (
        <Alert severity="info" icon={<DisconnectOutlined />} sx={{ mb: 2 }}>
          <Typography variant="body2">{intl.formatMessage({ id: 'form.msg.offlineNote' })}</Typography>
        </Alert>
      )}

      {/* Header with Add Button, Search, Filters, and Export */}
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Typography variant="h5">{intl.formatMessage({ id: 'form.skillTracking.targetTracking' })}</Typography>
          <Button variant="contained" size="small" startIcon={<PlusOutlined />} onClick={handleAddTarget}>
            {intl.formatMessage({ id: 'form.skillTracking.addNew' })}
          </Button>
        </Stack>

        {/* Search, Date Filters and Export */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" flexWrap="wrap">
          <DebouncedInput
            value={globalFilter ?? ''}
            onFilterChange={(value) => setGlobalFilter(String(value))}
            placeholder={intl.formatMessage({ id: 'form.skillTracking.searchPlaceholder' })}
            sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: 250 } }}
          />

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={intl.formatMessage({ id: 'form.skillTracking.fromDate' })}
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { minWidth: 150 }
                }
              }}
            />
            <DatePicker
              label={intl.formatMessage({ id: 'form.skillTracking.toDate' })}
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { minWidth: 150 }
                }
              }}
            />
          </LocalizationProvider>

          <CSVExport data={filteredTargets} filename={`target-tracking-${format(new Date(), 'yyyy-MM-dd')}.csv`} />
        </Stack>

        {/* Table View */}
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.lighter' }}>
                <TableCell sx={{ fontWeight: 600, width: 50 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>{intl.formatMessage({ id: 'form.label.target' })}</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>{intl.formatMessage({ id: 'form.skillTracking.domain' })}</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 120 }}>
                  {intl.formatMessage({ id: 'form.skillTracking.dateIntroduced' })}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, width: 120 }}>{intl.formatMessage({ id: 'form.skillTracking.dateMastered' })}</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 70 }} align="center">
                  {intl.formatMessage({ id: 'form.skillTracking.days' })}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>{intl.formatMessage({ id: 'form.skillTracking.location' })}</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>{intl.formatMessage({ id: 'form.skillTracking.people' })}</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>{intl.formatMessage({ id: 'form.skillTracking.stimuli' })}</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 60 }} align="center">
                  {intl.formatMessage({ id: 'form.skillTracking.actions' })}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {intl.formatMessage({ id: 'form.skillTracking.noRecordsFound' })}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row, index) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      bgcolor: row.isNew ? alpha(theme.palette.primary.main, 0.08) : 'inherit'
                    }}
                  >
                    <TableCell>{pagination.pageIndex * pagination.pageSize + index + 1}</TableCell>
                    <TableCell>
                      {row.isNew ? (
                        <TextField
                          size="small"
                          fullWidth
                          value={row.target || ''}
                          onChange={(e) => handleUpdateTarget(row.id, 'target', e.target.value)}
                          placeholder={intl.formatMessage({ id: 'form.skillTracking.enterTargetPlaceholder' })}
                        />
                      ) : (
                        <Typography variant="body2" fontWeight={600}>
                          {row.target}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.isNew ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={row.domain || ''}
                            displayEmpty
                            onChange={(e) => handleUpdateTarget(row.id, 'domain', e.target.value)}
                          >
                            <MenuItem value="" disabled>
                              {intl.formatMessage({ id: 'form.skillTracking.select' })}
                            </MenuItem>
                            {domainOptions.map((d) => (
                              <MenuItem key={d} value={d}>
                                {d}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Chip label={row.domain} size="small" color="secondary" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          value={row.dateIntroduced ? new Date(row.dateIntroduced) : null}
                          onChange={(newValue) => handleUpdateTarget(row.id, 'dateIntroduced', newValue)}
                          slotProps={{
                            textField: {
                              size: 'small',
                              fullWidth: true,
                              InputProps: {
                                readOnly: !row.isNew
                              }
                            }
                          }}
                        />
                      </LocalizationProvider>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                            value={row.dateMastered ? new Date(row.dateMastered) : null}
                            onChange={(newValue) => handleUpdateTarget(row.id, 'dateMastered', newValue)}
                            slotProps={{
                              textField: {
                                size: 'small',
                                fullWidth: true,
                                InputProps: {
                                  readOnly: !row.isNew
                                }
                              }
                            }}
                          />
                        </LocalizationProvider>
                        {/* Mastery status change history (rule engine vs person, and why). */}
                        {!row.isNew && <TargetStatusHistoryButton targetId={row.id} targetName={row.target || row.name} />}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color={typeof row.daysToMastery === 'number' ? 'primary' : 'text.secondary'}
                      >
                        {row.daysToMastery}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {row.isNew ? (
                        <TextField
                          size="small"
                          fullWidth
                          value={row.location || ''}
                          onChange={(e) => handleUpdateTarget(row.id, 'location', e.target.value)}
                          placeholder={intl.formatMessage({ id: 'form.skillTracking.locationShort' })}
                        />
                      ) : (
                        <Typography variant="body2">{row.location}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.isNew ? (
                        <TextField
                          size="small"
                          fullWidth
                          value={row.people || ''}
                          onChange={(e) => handleUpdateTarget(row.id, 'people', e.target.value)}
                          placeholder={intl.formatMessage({ id: 'form.skillTracking.peopleShort' })}
                        />
                      ) : (
                        <Typography variant="body2">{row.people}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.isNew ? (
                        <TextField
                          size="small"
                          fullWidth
                          value={row.stimuli || ''}
                          onChange={(e) => handleUpdateTarget(row.id, 'stimuli', e.target.value)}
                          placeholder={intl.formatMessage({ id: 'form.skillTracking.stimuliShort' })}
                        />
                      ) : (
                        <Typography variant="body2">{row.stimuli}</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        {!row.isNew ? (
                          <>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => {
                                setAllTargets(allTargets.map((t) => (t.id === row.id ? { ...t, isNew: true, isEditing: true } : t)));
                              }}
                            >
                              <EditOutlined />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleRemoveTarget(row.id)}>
                              <DeleteOutlined />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => {
                                setAllTargets(allTargets.map((t) => (t.id === row.id ? { ...t, isNew: false, isEditing: false } : t)));
                              }}
                            >
                              <CheckOutlined />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                if (row.isEditing) {
                                  // Cancel edit - revert to read-only
                                  setAllTargets(allTargets.map((t) => (t.id === row.id ? { ...t, isNew: false, isEditing: false } : t)));
                                } else {
                                  // New row - remove it
                                  handleRemoveTarget(row.id);
                                }
                              }}
                            >
                              {row.isEditing ? <CloseOutlined /> : <DeleteOutlined />}
                            </IconButton>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Stack sx={{ pt: 2 }}>
          <TablePagination
            getPageCount={() => Math.ceil(filteredTargets.length / pagination.pageSize)}
            setPageIndex={(pageIndex) => setPagination({ ...pagination, pageIndex })}
            setPageSize={(pageSize) => setPagination({ pageIndex: 0, pageSize })}
            getState={() => ({ pagination })}
          />
        </Stack>
      </Stack>
    </MainCard>
  );
};

SkillTrackingMode.propTypes = {
  viewMode: PropTypes.string,
  onDataChange: PropTypes.func,
  targets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      target: PropTypes.string.isRequired,
      domain: PropTypes.string,
      dateIntroduced: PropTypes.string,
      dateMastered: PropTypes.string,
      daysToMastery: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      location: PropTypes.string,
      people: PropTypes.string,
      stimuli: PropTypes.string
    })
  ),
  // Offline mode props
  isOfflineMode: PropTypes.bool,
  childId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  childName: PropTypes.string,
  therapistId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  therapistName: PropTypes.string,
  onSave: PropTypes.func,
  onBack: PropTypes.func
};

export default SkillTrackingMode;
